import React, { ChangeEventHandler } from 'react';
import PropTypes from 'prop-types';
import validate from 'validate.js';

import { Input, Alert, AlertLink } from '../DiComponents';

import type { InputProps } from '../DiComponents/Inputs';

interface IProps extends InputProps {
  onBlur?: ChangeEventHandler<HTMLInputElement>;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  validateEmail?: (string) => void;
  onValidate?: (value: null) => void;
  onClickDidYouMean?: (string) => void;
  handleBlur?: () => void;
  handleChange?: () => void;
  validationResult?: {
    didYouMean: string;
  };
  validateEmailResult?: object;
}

export default class DiEmailInput extends React.Component<IProps> {
  inputNode;

  static contextTypes = {
    formik: PropTypes.shape({
      handleBlur: PropTypes.func.isRequired,
      handleChange: PropTypes.func.isRequired,
    }),
  };

  onBlur = (event) => {
    this.validateEmail(event.target.value);
    if (this.props.onBlur) {
      this.props.onBlur(event);
    } else if (this.context.formik) {
      this.context.formik.handleBlur(event);
    }
  };

  onChange = (event) => {
    if (this.props.onValidate) {
      this.props.onValidate(null);
    }
    if (this.props.onChange) {
      this.props.onChange(event);
    } else if (this.context.formik) {
      this.context.formik.handleChange(event);
    }
  };

  onClickDidYouMean = () => {
    // istanbul ignore next
    if (this.inputNode) {
      this.inputNode.focus();
    }
    if (this.props.onValidate) {
      this.props.onValidate(null);
    }
    if (this.props.onClickDidYouMean && this.props.validationResult) {
      this.props.onClickDidYouMean(this.props.validationResult.didYouMean);
    }
  };

  setInputRef = (node) => {
    // istanbul ignore next
    this.inputNode = node;
  };

  async validateEmail(email) {
    const { onValidate } = this.props;
    if (!onValidate) {
      return;
    }
    // If the email doesn't pass a simple format check, we’ll skip calling the API to validate,
    // which is mostly useful for verifying the existence of the actual email address.
    const errors = validate.single(email, { email: true });
    if (errors) {
      return;
    }
    // If there are any errors or no results just fail silently as we don't want this preventing
    // an appointment from being scheduled
    let result;
    try {
      result =
        this.props.validateEmail && (await this.props.validateEmail({ variables: { email } }));
    } catch (error) {
      return;
    }

    const { data } = result || {};
    if (!data) {
      return;
    }
    onValidate(data.validateEmail);
  }

  render() {
    /* eslint-disable  @typescript-eslint/no-unused-vars */
    const {
      validateEmail,
      validationResult,
      onValidate,
      onClickDidYouMean,
      validateEmailResult,
      ...props
    } = this.props;
    return (
      <React.Fragment>
        <Input
          type="email"
          {...props}
          onBlur={this.onBlur}
          onChange={this.onChange}
          ref={this.setInputRef}
        />
        {validationResult && validationResult.didYouMean && (
          <Alert type="warning" subtle fontSize="xs" marginTop="xs">
            Did you mean{' '}
            <AlertLink onClick={this.onClickDidYouMean}>{validationResult.didYouMean}</AlertLink>?
          </Alert>
        )}
      </React.Fragment>
    );
  }
}
